import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {UserModel} from 'src/app/customer/customer-shared/user.model';
import {UserService} from './user.service';

@Injectable({
    providedIn: 'root'
})

export class AuthenticationService {

    currentUser: any = null;
    origUser: any = null;

    constructor(private http: HttpClient, private userService: UserService) {
        this.currentUser = JSON.parse(localStorage.getItem('currentUser'));
    }

    getUser() {
        return this.currentUser;
    }

    isLoggedIn() {
        return this.currentUser;
    }

    timeUntilExpiry() {
        if (this.currentUser && this.currentUser.expires) {
            const ts = Math.round(new Date().getTime() / 1000);
            return this.currentUser.expires - ts;
        }
        return -1;
    }

    isExpired() {
        const ts = Math.round(new Date().getTime() / 1000);
        return this.currentUser && ts > this.currentUser.expires;
    }

    login(email: string, password: string): Observable<boolean> {
        return this.http.post<any>(environment.apiUrl + '/login/token', { email, password })
            .pipe(map(response => {
                const token = response.token;
                const expires = response.expires;
                // login successful if there's a jwt token in the response
                if (token) {
                    // store email and jwt token in local storage to keep user logged in between page refreshes
                    this.currentUser = { token, expires, ...response.user };
                    localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
                    if (this.currentUser.role == 3) {
                        this.userService.getUserDisplayPermissions(this.currentUser.id).subscribe(udp => {
                            this.currentUser.udp = udp;
                        }, error => {
                            console.log(error);
                            this.currentUser.udp = {
                                id: undefined,
                                user_id: undefined,
                                coins_allowed: false,
                                loyalty_allowed: false,
                                cancelled_allowed: false,
                                mifare_allowed: false,
                                club_allowed: false
                            };
                        }, () => {
                            localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
                            // return true to indicate successful login
                            return true;
                        });
                    } else {
                        localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
                    }
                } else {
                    // return false to indicate failed login
                    return false;
                }
            }));
    }

    impersonateLogin(email: string): Observable<boolean> {
        return this.http.post<any>(environment.apiUrl + '/login/impersonate', { email })
            .pipe(map(response => {
                const token = response.token;
                const expires = response.expires;
                // login successful if there's a jwt token in the response
                if (token) {
                    // store email and jwt token in local storage to keep user logged in between page refreshes
                    this.currentUser = { token, expires, ...response.user };
                    localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
                    if (this.currentUser.role == 3) {
                        this.userService.getUserDisplayPermissions(this.currentUser.id).subscribe(udp => {
                            this.currentUser.udp = udp;
                        }, error => {
                            console.log(error);
                            this.currentUser.udp = {
                                id: undefined,
                                user_id: undefined,
                                coins_allowed: false,
                                loyalty_allowed: false,
                                cancelled_allowed: false,
                                mifare_allowed: false,
                                club_allowed: false,
                                integration_allowed: false,
                                voucher_allowed: false,
                                aarskort_allowed: false,
                                loyaltyuser_allowed: false,
                                mdbprize_allowed: false,
                                csv_allowed: false,
                                measurings_allowed: false,
                                pdf_certifikat: false,
                                email_notification: false,
                                loyalty_products_allowed: false,
                                pos_access: false
                            };
                        }, () => {
                            localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
                            // return true to indicate successful login
                            return true;
                        });
                    } else {
                        localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
                    }
                } else {
                    // return false to indicate failed login
                    return false;
                }
            }));
    }

    getToken(): string {
        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        return currentUser && currentUser.token;
    }

    public isAdmin(): boolean {
        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        const role = currentUser && currentUser.role;
        return role === 1;
    }

    public loyaltyAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.loyalty_allowed);
    }

    public loyaltyProductsAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.loyalty_products_allowed);
    }

    public voucherAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.voucher_allowed);
    }

    public measuringsAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.measurings_allowed);
    }

    public clubAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.club_allowed);
    }

    public mifareAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.mifare_allowed);
    }

    public aarskortAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.aarskort_allowed);
    }

    public integrationAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.integration_allowed);
    }

    public mdbPrizeAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.mdbprize_allowed);
    }

    public posAccessAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.pos_access);
    }

    public emailNotificationAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.email_notification);
    }

    public pdfCertificateAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.pdf_certifikat);
    }

    public csvAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.csv_allowed);
    }

    public coinsAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.coins_allowed);
    }

    public cancelledAllowed(): boolean {
        return this.isAdmin() || (this.currentUser && this.currentUser.udp && this.currentUser.udp.cancelled_allowed);
    }

    // Returns the logged-in user's customer id as a number, or null when unavailable.
    // Reads the in-memory user (single source of truth, consistent with getUser()) and
    // coerces the value, so callers can rely on a real number — some APIs deliver
    // customer_id as a string, which silently breaks strict-equality consumers (e.g. a
    // PrimeNG dropdown's selected-option match).
    public getCustomerId(): number {
        const user = this.getUser();
        if (!user || user.customer_id == null) {
            return null;
        }
        return Number(user.customer_id);
    }

    logout(): void {
        this.currentUser = null;
        localStorage.removeItem('currentUser');
        localStorage.removeItem('origUser');
    }

    impersonate(user: UserModel): Observable<boolean> {
        const currentUser = JSON.parse(localStorage.getItem('currentUser'));
        this.origUser = currentUser;
        localStorage.setItem('origUser', JSON.stringify(currentUser));
        return this.impersonateLogin(user.email);
    }

    stopImpersonate() {
        try {
            const originalUser = localStorage.getItem('origUser');
            localStorage.setItem('currentUser', originalUser);
            this.currentUser = JSON.parse(localStorage.getItem('currentUser'));
            localStorage.removeItem('origUser');
            this.origUser = null;
            return true;
        } catch (exception) {
            console.log(exception);
            return false;
        }

    }

    isImpersonating(): boolean {
        const originalUser = JSON.parse(localStorage.getItem('origUser'));
        return !!originalUser;
    }
}
